import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Button from '../components/UI/Button';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 flex items-center">
          <Button 
            variant="ghost" 
            className="mr-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Politique de Confidentialité</h1>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 space-y-8">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Dernière mise à jour : 28 mars 2025
          </div>

          <section>
            <p className="text-gray-600 dark:text-gray-400">
              Chez HLH Invest Corp, nous attachons une grande importance à la confidentialité de vos données. 
              La présente politique de confidentialité a pour but de vous informer de la manière dont nous collectons, 
              utilisons, stockons et protégeons vos informations personnelles lorsque vous utilisez notre site ou nos services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">1. Responsable du traitement</h2>
            <p className="text-gray-600 dark:text-gray-400">
              HLH Invest Corp<br />
              651 N Broad St, New Castle, DE 19709, États-Unis<br />
              Email : contact@invoice-ai.fr
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">2. Données collectées</h2>
            <p className="text-gray-600 dark:text-gray-400">Nous pouvons collecter les données suivantes :</p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 dark:text-gray-400 space-y-2">
              <li>Nom, prénom (si renseigné)</li>
              <li>Adresse email</li>
              <li>Informations de facturation (via Stripe)</li>
              <li>Informations liées à l'utilisation du service (ex : nombre de factures générées, date de création)</li>
              <li>Adresse IP, type de navigateur, données de navigation (via cookies ou outils d'analyse)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">3. Finalité de la collecte</h2>
            <p className="text-gray-600 dark:text-gray-400">Ces données sont collectées pour :</p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 dark:text-gray-400 space-y-2">
              <li>Fournir le service demandé (génération de factures)</li>
              <li>Gérer les paiements et abonnements</li>
              <li>Améliorer l'expérience utilisateur</li>
              <li>Assurer la sécurité du site</li>
              <li>Envoyer des communications (si consentement donné)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">4. Base légale du traitement</h2>
            <p className="text-gray-600 dark:text-gray-400">Nous traitons vos données :</p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 dark:text-gray-400 space-y-2">
              <li>Sur la base de votre consentement</li>
              <li>Pour l'exécution d'un contrat (abonnement)</li>
              <li>Pour respecter nos obligations légales</li>
              <li>Pour notre intérêt légitime (amélioration du service, prévention des fraudes)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">5. Partage des données</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Vos données ne sont jamais vendues à des tiers. Elles peuvent être partagées uniquement avec :
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 dark:text-gray-400 space-y-2">
              <li>Notre prestataire de paiement (Stripe)</li>
              <li>Notre hébergeur (Hostinger)</li>
              <li>Outils tiers nécessaires au fonctionnement du service (analytics, email, etc.)</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Tous les prestataires sont soumis à des obligations strictes de confidentialité.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">6. Durée de conservation</h2>
            <p className="text-gray-600 dark:text-gray-400">Les données sont conservées :</p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 dark:text-gray-400 space-y-2">
              <li>Pendant toute la durée de votre abonnement</li>
              <li>Jusqu'à 3 ans après la fin du contrat à des fins administratives ou légales</li>
              <li>Ou immédiatement sur demande de suppression</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">7. Sécurité</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données 
              (chiffrement, pare-feu, accès restreint, etc.).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">8. Vos droits</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc pl-6 mt-2 text-gray-600 dark:text-gray-400 space-y-2">
              <li>Droit d'accès</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement ("droit à l'oubli")</li>
              <li>Droit d'opposition</li>
              <li>Droit à la portabilité</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Vous pouvez exercer ces droits en nous contactant à contact@invoice-ai.fr
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">9. Cookies</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Notre site utilise des cookies à des fins de fonctionnement et d'analyse. 
              Vous pouvez modifier vos préférences à tout moment via votre navigateur.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">10. Modifications</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Cette politique peut être mise à jour à tout moment. Les utilisateurs seront informés en cas de 
              modification significative.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;